import { httpsCallable } from 'firebase/functions';
import { getFbFunctions } from './development';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  getDocs,
  getFirestore,
} from 'firebase/firestore';

import { getApp } from 'firebase/app';
import type { State, WebhookDefinition, WebhookResponse } from './types/index.ts';

function webhookRef(context: State) {
  const fbApp = getApp(context.firebaseApp);
  const db = getFirestore(fbApp, context.dbid);

  if (context.team && context.userMode === 'team') {
    return collection(db, 'teams', context.team, 'webHooks');
  } else if (context.user) {
    return collection(db, 'users', context.user.uid, 'webHooks');
  } else {
    return null;
  }
}

export async function testWebHook(data: WebhookDefinition): Promise<WebhookResponse> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  const tempData = {
    ...data,
    event: data.events[0],
  };
  const send = httpsCallable(functions, 'webhooks-test');
  const result = await send(tempData) as WebhookResponse;
  return result;
}

export async function addWebHook(data: WebhookDefinition): Promise<WebhookDefinition | false> {
  try {
    const docRef = webhookRef(this);

    if (docRef == null) {
      return false;
    }

    const webhook = await addDoc(docRef, {
      url: data.url,
      name: data.name,
      events: data.events,
      body: data.body,
      headers: data.headers,
      method: data.method,
    });

    const result = {
      id: webhook.id,
      url: data.url,
      name: data.name,
      events: data.events,
      body: data.body,
      headers: data.headers,
      method: data.method,
    } as WebhookDefinition;
    return result;
  } catch {
    return false;
  }
}

export async function editWebHook(data: WebhookDefinition): Promise<WebhookDefinition | false> {
  try {
    const id = data.id;
    const webhook = data;
    delete webhook.id;

    const docRef = webhookRef(this);

    if (docRef == null) {
      return false;
    }

    await updateDoc(doc(docRef, id), webhook as any);

    const result = {
      id: id,
      url: webhook.url,
      name: webhook.name,
      events: webhook.events,
      body: webhook.body,
      headers: webhook.headers,
      method: webhook.method,
    } as WebhookDefinition;

    return result;
  } catch {
    return false;
  }
}

export async function deleteWebHook(id: string): Promise<boolean> {
  const docRef = webhookRef(this);

  if (docRef == null) {
    return false;
  }

  try {
    await deleteDoc(doc(docRef, id));
    return true;
  }
  catch {
    return false;
  }
}

export async function getWebHooks() {
  // Get list of webhook documents and return them
  const docRef = webhookRef(this);

  if (docRef == null) {
    return false;
  }

  const querySnapshot = await getDocs(docRef);

  if (querySnapshot.docs.length > 0) {
    const result: WebhookDefinition[] = [];

    querySnapshot.forEach((doc) => {
      result.push({
        id: doc.id,
        url: doc.data().url,
        name: doc.data().name,
        events: doc.data().events,
        body: doc.data().body,
        headers: doc.data().headers,
        method: doc.data().method,
        status: doc.data().lastResponse ? doc.data().lastResponse : null,
        timestamp: doc.data().lastSuccess ? doc.data().lastSuccess : null,
      });
    });

    return result;

  } else {
    return false;
  }
}
